import { BoundaryReview } from '@/screens/parts/BoundaryApproval';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, RotateCcw, ShieldAlert, X } from 'lucide-react';
import {
  Drawer, Field, LiveScreen, QueryState, StatusPill,
  formatDateTime, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { ApprovalPinField, PIN_RESULTS, isPinResult } from '@/components/ApprovalPin';
import { Lock } from 'lucide-react';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { OTP_FAILURES, sendOtp, verifyOtp, type IssuedOtp } from '@/api/otp';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import type { ApprovalRow } from '@/types/records';

type Decision = 'approved' | 'rejected' | 'returned';

/** The request statuses the database still accepts a decision on. */
const OPEN = ['submitted', 'under_review', 'returned'];

/**
 * The approval inbox.
 *
 * Every rule here is the database's. `api.fn_can_decide_approval` answers whether this caller
 * may decide THIS request and why not - self-approval, the wrong role, a request already
 * closed - so the screen asks rather than guessing, and a reason is shown instead of a
 * disabled button with no explanation.
 *
 * A step marked `requires_mfa` needs a freshly verified code before the decision is accepted.
 * That is checked again on the server: the event must belong to the approver, be for step-up,
 * be verified, be under fifteen minutes old, and not already have been spent on another
 * approval.
 */
export default function ApprovalInboxScreen() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<ApprovalRow | null>(null);
  const [stat, setStat] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulk, setBulk] = useState<Decision | null>(null);

  const inbox = useRows<ApprovalRow>('approval_inbox', {
    orderBy: { column: 'submitted_at', ascending: false },
  });

  // The view returns closed requests as well as open ones, and its `is_overdue` is set on
  // anything past its due date - so Overdue counts only requests still waiting on a decision.
  const STATS = useMemo(() => [
    { key: 'all', label: 'Requests', match: () => true, tone: 'neutral' as const },
    { key: 'submitted', label: 'Submitted', tone: 'warn' as const, match: (row: ApprovalRow) => row.request_status === 'submitted' },
    { key: 'under_review', label: 'Under review', tone: 'info' as const, match: (row: ApprovalRow) => row.request_status === 'under_review' },
    { key: 'returned', label: 'Returned', tone: 'warn' as const, match: (row: ApprovalRow) => row.request_status === 'returned' },
    { key: 'overdue', label: 'Overdue', tone: 'error' as const,
      match: (row: ApprovalRow) => Boolean(row.is_overdue) && OPEN.includes(row.request_status) },
    { key: 'approved', label: 'Approved', tone: 'ok' as const, match: (row: ApprovalRow) => row.request_status === 'approved' },
    { key: 'rejected', label: 'Rejected', tone: 'error' as const, match: (row: ApprovalRow) => row.request_status === 'rejected' },
  ], []);

  const visible = useMemo(() => applyStat(inbox.data ?? [], STATS, stat), [inbox.data, STATS, stat]);

  const columns: Array<Column<ApprovalRow>> = [
    { key: 'entity_label', label: 'Record', render: (row) => (
      <span>{row.is_locked && <Lock size={12} aria-label="Locked" />} {row.entity_label ?? row.entity_id.slice(0, 8)}</span>
    ) },
    { key: 'submitted_by_name', label: 'Submitted by', render: (row) => row.submitted_by_name ?? '--' },
    { key: 'workflow_name', label: 'Workflow', render: (row) => row.workflow_name ?? '--' },
    { key: 'step_name', label: 'Step', render: (row) => row.step_name ?? `Step ${row.current_step_no}` },
    { key: 'submitted_at', label: 'Submitted', render: (row) => formatDateTime(row.submitted_at) },
    {
      key: 'due_at', label: 'Due',
      render: (row) => (row.is_overdue
        ? <span style={{ color: 'var(--error)' }}><AlertTriangle size={13} /> Overdue</span>
        : formatDateTime(row.due_at)),
    },
    { key: 'request_status', label: 'Status', render: (row) => <StatusPill value={row.request_status} /> },
  ];

  return (
    <LiveScreen
      subtitle="Records waiting on a decision. What you may decide, and why not, comes from the server."
      title="Approval inbox"
    >
      <StatCards active={stat} onSelect={setStat} rows={inbox.data ?? []} stats={STATS} />

      <QueryState empty="Nothing is waiting on you." query={inbox}>
        <DataGrid
          bulkActions={() => (
            <>
              <button className="primary" onClick={() => setBulk('approved')} type="button"><Check size={14} /> Approve</button>
              <button onClick={() => setBulk('returned')} type="button"><RotateCcw size={14} /> Return</button>
              <button className="danger" onClick={() => setBulk('rejected')} type="button"><X size={14} /> Reject</button>
            </>
          )}
          columns={columns}
          exportName="approvals"
          onRowClick={setOpen}
          onSelectionChange={setSelected}
          rows={visible}
          screenKey="approval.inbox"
          selectable={(row) => OPEN.includes(row.request_status)}
          selectedId={open?.id}
          selectedIds={selected}
        />
      </QueryState>

      {open && (
        <DecisionDrawer
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            void queryClient.invalidateQueries({ queryKey: ['approval_inbox'] });
            void queryClient.invalidateQueries({ queryKey: ['plot_harvest_entries'] });
            void queryClient.invalidateQueries({ queryKey: ['plot_stock_summary'] });
          }}
          request={open}
        />
      )}
      {bulk && (
        <BulkDecisionDrawer
          decision={bulk}
          ids={selected}
          labels={Object.fromEntries((inbox.data ?? []).map((row) => [row.id, row.entity_label ?? row.id.slice(0, 8)]))}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null);
            setSelected([]);
            void queryClient.invalidateQueries({ queryKey: ['approval_inbox'] });
          }}
        />
      )}
    </LiveScreen>
  );
}

const BLOCKING_REASONS: Record<string, string> = {
  request_not_found: 'This request is no longer available to you.',
  self_approval_blocked: 'You submitted this record, so you cannot approve it. Maker and checker must differ.',
  approver_role_missing: 'This step is decided by a different role.',
  not_assigned_approver: 'This step is assigned to a specific person.',
  workflow_step_missing: 'The workflow has no step configured at this point. Ask an admin to check it.',
  no_active_user: 'Sign in again and retry.',
  not_next_level: 'This change is decided by the next level up - a role above the one that made it.',
};

const DECISION_WORD: Record<Decision, string> = { approved: 'Approve', rejected: 'Reject', returned: 'Return' };

/**
 * Bulk decisions (owner, 2026-09-21: "bulk approval option for datagrid"). One PIN for the lot;
 * each request is still decided on its own by the server, and any it will not take (a step that
 * needs step-up MFA, a change you made yourself) is listed with the reason rather than failing
 * the rest.
 */
function BulkDecisionDrawer({ ids, labels, decision, onClose, onDone }: {
  ids: string[]; labels: Record<string, string>; decision: Decision; onClose: () => void; onDone: () => void;
}) {
  const [remarks, setRemarks] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [results, setResults] = useState<Array<{ approval_request_id: string | null; outcome: string }> | null>(null);

  async function run() {
    if (decision !== 'approved' && !remarks.trim()) { setError('Say why, so the people who submitted these know what to change.'); return; }
    if (!pin) { setError('Enter your approval PIN.'); return; }
    setError(undefined);
    setBusy(true);
    try {
      const rows = await callRpc<Array<{ approval_request_id: string | null; outcome: string }>>('fn_bulk_decide_approvals', {
        p_request_ids: ids, p_decision: decision, p_remarks: remarks.trim() || null, p_pin: pin,
      });
      const first = rows[0];
      if (rows.length === 1 && first && first.approval_request_id === null && isPinResult(first.outcome)) {
        setError(PIN_RESULTS[first.outcome]);
        setPin('');
        return;
      }
      const done = rows.filter((row) => !row.outcome.startsWith('error')).length;
      toast.success(`${done} of ${rows.length} recorded.`);
      if (done === rows.length) onDone(); else setResults(rows);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The decisions could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      footer={results
        ? <button className="primary" onClick={onDone} type="button">Done</button>
        : <>
            <button className="cancel" onClick={onClose} type="button">Cancel</button>
            <button className={decision === 'rejected' ? 'danger' : 'primary'} disabled={busy} onClick={() => void run()} type="button">
              {busy ? 'Recording...' : `${DECISION_WORD[decision]} ${ids.length}`}
            </button>
          </>}
      onClose={onClose}
      open
      subtitle={`${ids.length} request${ids.length === 1 ? '' : 's'}`}
      title={`${DECISION_WORD[decision]} selected`}
    >
      {results ? (
        <ul className="bulk-results">
          {results.map((row) => (
            <li className={row.outcome.startsWith('error') ? 'bad' : 'good'} key={row.approval_request_id ?? 'pin'}>
              <strong>{row.approval_request_id ? labels[row.approval_request_id] ?? row.approval_request_id.slice(0, 8) : ''}</strong>
              <span>{row.outcome.replace(/^error: (Approval blocked: )?/, '').replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <ul className="bulk-results">
            {ids.map((id) => <li key={id}>{labels[id] ?? id.slice(0, 8)}</li>)}
          </ul>
          <Field hint={decision === 'approved' ? 'Optional.' : 'Required - it goes to everyone whose request this is.'} label="Remarks">
            <textarea onChange={(event) => setRemarks(event.target.value)} rows={3} value={remarks} />
          </Field>
          <ApprovalPinField onChange={setPin} value={pin} />
          {error && <p className="login-error" role="alert">{error}</p>}
        </>
      )}
    </Drawer>
  );
}

function DecisionDrawer({
  request,
  onClose,
  onDone,
}: { request: ApprovalRow; onClose: () => void; onDone: () => void }) {
  const [remarks, setRemarks] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [mfa, setMfa] = useState<IssuedOtp | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEventId, setMfaEventId] = useState<string | null>(null);

  const permission = useQuery({
    queryKey: ['can-decide', request.id],
    queryFn: () => callRpc<Array<{ allowed: boolean; blocking_reason: string | null; requires_mfa: boolean }>>(
      'fn_can_decide_approval', { p_approval_request_id: request.id }),
    enabled: isSupabaseConfigured,
  });

  const verdict = permission.data?.[0];
  const allowed = verdict?.allowed ?? false;
  const requiresMfa = verdict?.requires_mfa ?? false;

  async function sendStepUp() {
    setError(undefined);
    setBusy(true);
    try {
      const issued = await sendOtp({ purpose: 'step_up', channel: 'whatsapp' });
      setMfa(issued);
      toast.success(`Verification code sent to ${issued.destination_masked}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyStepUp() {
    if (!mfa) return;
    setError(undefined);
    setBusy(true);
    try {
      const result = await verifyOtp(mfa.otp_event_id, mfaCode);
      if (!result.verified) {
        setError(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code could not be verified.');
        return;
      }
      setMfaEventId(mfa.otp_event_id);
      toast.success('Verified. You can record the decision now.');
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: Decision) {
    // The server requires remarks on anything that is not an approval, and it is right to:
    // "rejected, no reason given" is not a decision anyone can act on.
    if (decision !== 'approved' && !remarks.trim()) {
      setError('Say why, so the person who submitted it knows what to change.');
      return;
    }
    if (!pin) { setError('Enter your approval PIN.'); return; }
    setError(undefined);
    setBusy(true);
    try {
      const outcome = await callRpc<string>('fn_decide_approval', {
        p_approval_request_id: request.id,
        p_decision: decision,
        p_remarks: remarks.trim() || null,
        p_mfa_event_id: mfaEventId,
        p_pin: pin,
      });
      if (isPinResult(outcome)) { setError(PIN_RESULTS[outcome]); setPin(''); return; }
      toast.success(`Recorded: ${outcome.replace(/_/g, ' ')}${decision === 'approved' && request.workflow_code === 'RECORD_CHANGE' ? ' - the record is now locked' : ''}.`);
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The decision could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  const mfaSatisfied = !requiresMfa || Boolean(mfaEventId);

  return (
    <Drawer
      footer={
        <>
          <button onClick={onClose} type="button">Close</button>
          <button className="danger" disabled={!allowed || busy || !mfaSatisfied}
                  onClick={() => void decide('rejected')} type="button">
            <X size={15} /> Reject
          </button>
          <button disabled={!allowed || busy || !mfaSatisfied} onClick={() => void decide('returned')} type="button">
            <RotateCcw size={15} /> Return
          </button>
          <button className="primary" disabled={!allowed || busy || !mfaSatisfied}
                  onClick={() => void decide('approved')} type="button">
            <Check size={15} /> Approve
          </button>
        </>
      }
      onClose={onClose}
      open
      subtitle={request.workflow_name ?? undefined}
      title={request.entity_label ?? 'Approval request'}
    >
      <dl className="detail-grid">
        <div><dt>Record type</dt><dd>{request.entity_table}</dd></div>
        <div><dt>Step</dt><dd>{request.step_name ?? `Step ${request.current_step_no}`}</dd></div>
        <div><dt>Submitted</dt><dd>{formatDateTime(request.submitted_at)}</dd></div>
        <div><dt>Status</dt><dd><StatusPill value={request.request_status} /></dd></div>
        <div><dt>Submitted by</dt><dd>{request.submitted_by_name ?? '--'}</dd></div>
      </dl>

      {request.entity_table === 'geo.plot_geometries' && <BoundaryReview geometryId={request.entity_id} />}

      {permission.isLoading && <p className="state-note">Checking what you may do...</p>}

      {verdict && !allowed && (
        <p className="login-alert" role="alert">
          {BLOCKING_REASONS[verdict.blocking_reason ?? ''] ?? verdict.blocking_reason ?? 'You cannot decide this request.'}
        </p>
      )}

      {allowed && requiresMfa && !mfaEventId && (
        <div className="card" style={{ display: 'grid', gap: 10 }}>
          <strong><ShieldAlert size={15} /> This step needs a verification code</strong>
          <p className="muted" style={{ margin: 0 }}>
            A code goes to your own registered number. It is valid for fifteen minutes and is
            spent on this one decision.
          </p>
          {!mfa
            ? <button disabled={busy} onClick={() => void sendStepUp()} style={{ justifySelf: 'start' }} type="button">
                {busy ? 'Sending...' : 'Send my code'}
              </button>
            : (
              <>
                <Field label="Your code">
                  <input className="login-code" inputMode="numeric" maxLength={6}
                         onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                         placeholder="000000" value={mfaCode} />
                </Field>
                <button disabled={busy || mfaCode.length !== 6} onClick={() => void verifyStepUp()}
                        style={{ justifySelf: 'start' }} type="button">
                  Verify
                </button>
              </>
            )}
        </div>
      )}

      <Field hint="Required for a rejection or a return." label="Remarks">
        <textarea onChange={(event) => setRemarks(event.target.value)} rows={3} value={remarks} />
      </Field>
      {allowed && <ApprovalPinField onChange={setPin} value={pin} />}

      {error && <p className="login-error" role="alert">{error}</p>}
    </Drawer>
  );
}
